import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  EMPRESAS, EMPRESA_META, FASES, FASE_META, getEntity, temMotorista,
  type Empresa, type Fase,
} from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { Badge, Card, ErrorBox, Spinner, Table, brl, brlCompact, num } from "../components/ui.js";
import { Modal } from "../components/Modal.js";
import { OrdemColetaModal } from "../components/OrdemColetaModal.js";
import { FormEngine } from "../components/FormEngine.js";
import { fmtColetaDia, marcadores, ordenarPorColeta, situacaoHorario } from "../lib/carga.js";

// A torre olha para esta tela o dia inteiro; sem recarga automática ela
// mostraria o estado de quando a aba foi aberta — o oposto de "tempo real".
const REFRESH_MS = 30_000;

interface Celula {
  empresa: string;
  fase: string;
  viagens: number;
  peso: number;
  frete: number;
  sem_motorista: number;
  com_sugestao_positiva?: number;
  atrasos: number;
}
interface Resumo {
  competencia: string | null;
  competencias: string[];
  celulas: Celula[];
  atualizadoEm: string;
}

const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];
// "2026-06-01" → "junho/2026". Fatiamos a string em vez de usar Date: o
// construtor lê "2026-06-01" como UTC e, em fuso negativo, exibiria maio.
const rotuloCompetencia = (c: string) => `${MESES[Number(c.slice(5, 7)) - 1]}/${c.slice(0, 4)}`;
const fmtDateTime = (v: unknown) => (v ? String(v).replace("T", " ").slice(0, 16) : "—");
const fmtHora = (v: unknown) => (v ? String(v).replace("T", " ").slice(11, 16) || "—" : "—");

/** Selos urgente/prioridade/crítica/frete especial numa célula da grade. */
function Selos({ row }: { row: Record<string, unknown> }) {
  const m = marcadores(row);
  if (!m.length) return <span className="text-slate-300">—</span>;
  return (
    <span className="flex flex-wrap gap-1" data-testid="selos-carga">
      {m.map((x) => <span key={x.chave} className={`whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${x.cls}`}>{x.label}</span>)}
    </span>
  );
}

const SITUACAO_HORARIO = {
  "sem-pedido": null,
  aguardando: { label: "aguardando a torre", cls: "text-amber-700" },
  confirmado: { label: "confirmado", cls: "text-emerald-700" },
  alterado: { label: "alterado pela torre", cls: "text-sky-700" },
} as const;

/** Horário pedido pelo gestor, com o que a programação fez com ele. */
function Solicitado({ row }: { row: Record<string, unknown> }) {
  const sit = situacaoHorario(row);
  if (sit === "sem-pedido") return <span className="text-slate-300">—</span>;
  const meta = SITUACAO_HORARIO[sit];
  return (
    <span className="whitespace-nowrap" data-testid="horario-solicitado" data-situacao={sit}>
      {fmtColetaDia(row.data_hora_solicitada)}
      <br /><span className={`text-[11px] ${meta.cls}`}>{meta.label}</span>
    </span>
  );
}

/** Modal do gestor: pede um horário; quem confirma é a torre. */
function SolicitarHorario({ row, onClose }: { row: Record<string, unknown>; onClose: () => void }) {
  const qc = useQueryClient();
  const inicial = row.data_hora_solicitada ? String(row.data_hora_solicitada).replace(" ", "T").slice(0, 16) : "";
  const [valor, setValor] = useState(inicial);
  const enviar = useMutation({
    mutationFn: async (v: string | null) =>
      (await api.post(`/operacoes/${row.id}/solicitar-horario`, { data_hora_solicitada: v })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["torre-grid"] });
      onClose();
    },
  });
  return (
    <Modal title={`Solicitar horário — ordem ${String(row.numero_ordem ?? row.id)}`} onClose={onClose}>
      <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
        Este é um <b>pedido</b>. O horário de coleta só vale depois que a programação, na Torre de Controle,
        confirmar ou alterar.
      </p>
      <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="pedido-horario">Data e hora desejadas</label>
      <input id="pedido-horario" type="datetime-local" className="input max-w-xs" value={valor} onChange={(e) => setValor(e.target.value)} data-testid="pedido-horario" />
      {row.data_hora_coleta ? (
        <p className="mt-2 text-xs text-slate-500">Coleta confirmada hoje: <b>{fmtColetaDia(row.data_hora_coleta)}</b></p>
      ) : null}
      {enviar.isError && <div className="mt-3"><ErrorBox error={enviar.error} /></div>}
      <div className="mt-4 flex gap-2">
        <button type="button" className="btn-primary" disabled={!valor || enviar.isPending} onClick={() => enviar.mutate(valor.slice(0, 16))} data-testid="pedido-enviar">
          {enviar.isPending ? "Enviando…" : "Solicitar horário"}
        </button>
        {inicial && (
          <button type="button" className="btn-secondary" disabled={enviar.isPending} onClick={() => enviar.mutate(null)}>Retirar pedido</button>
        )}
        <button type="button" className="ml-auto text-sm text-slate-500 hover:underline" onClick={onClose}>Cancelar</button>
      </div>
    </Modal>
  );
}

/** Barra empilhada com a proporção das três fases — o "raio-x" da empresa. */
function BarraFases({ porFase, total }: { porFase: Record<Fase, Celula | undefined>; total: number }) {
  if (!total) return <div className="h-2 w-full rounded-full bg-slate-100" />;
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
      {FASES.map((f) => {
        const n = porFase[f]?.viagens ?? 0;
        if (!n) return null;
        return (
          <div
            key={f}
            className={FASE_META[f].bar}
            style={{ width: `${(n / total) * 100}%` }}
            title={`${FASE_META[f].label}: ${n}`}
          />
        );
      })}
    </div>
  );
}

/** Chip de contagem por fase, usado no cabeçalho fechado e nas abas. */
function ChipFase({ fase, n, ativo, onClick, testId }: {
  fase: Fase; n: number; ativo?: boolean; onClick?: () => void; testId: string;
}) {
  const m = FASE_META[fase];
  // `transition-colors`, não `transition`: o utilitário genérico do Tailwind
  // inclui `box-shadow`, e o anel da aba ativa É um box-shadow. Animá-lo
  // promove a pastilha a uma camada de composição própria, e trocar de aba
  // deixava por ~150 ms o anel desenhado na aba ANTERIOR enquanto a lista
  // abaixo já era da nova (visível em screenshot de página inteira). Numa aba
  // clicada às pressas, o operador lê a fase errada. Cor pode animar; o
  // indicador de seleção tem que ser instantâneo.
  const base = "rounded-full px-3 py-1 text-xs font-semibold transition-colors";
  // A aba INATIVA fica cinza, não colorida-com-opacidade. Diferenciar duas
  // pastilhas coloridas só por opacidade e um anel não funciona: o azul de
  // "disponíveis" continua parecendo mais aceso que o âmbar de "agendadas"
  // mesmo apagado, e dá para ler a aba errada como selecionada.
  const cls = onClick
    ? ativo
      ? `${base} ${m.tone} ring-2 ring-brand-600 ring-offset-1`
      : `${base} bg-slate-100 text-slate-500 hover:bg-slate-200`
    : `${base} ${m.tone}`; // cabeçalho: informativo, nunca "selecionado"
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid={testId}
      aria-pressed={onClick ? ativo : undefined}
      className={cls}
    >
      {num(n)} {m.label.toLowerCase()}
    </button>
  );
}

/**
 * Painel de uma empresa: cabeçalho sempre visível com o panorama e, quando
 * aberto, as três abas de fase com a grade.
 *
 * O panorama fica no cabeçalho FECHADO de propósito. No portal de referência
 * as seções fechadas não dizem nada — é preciso abrir uma a uma para saber
 * onde está o movimento. Aqui as contagens ficam à vista e abrir é só para
 * ver as linhas.
 */
function PainelEmpresa({
  empresa, celulas, aberto, onToggle, competencia, vincular,
}: {
  empresa: Empresa;
  celulas: Celula[];
  aberto: boolean;
  onToggle: () => void;
  competencia: string | null;
  vincular: boolean;
}) {
  const { canWrite, can } = useAuth();
  const qc = useQueryClient();
  const writable = canWrite("viagem_vega");
  // Quem vincula sem agendar (gestor de frota) não edita a carga: só SOLICITA
  // horário (POST solicitar-horario). Ver D-27.
  const podeSolicitar = !writable && can("vinculo");
  const [pedindo, setPedindo] = useState<Record<string, unknown> | null>(null);
  const [fase, setFase] = useState<Fase>("DISPONIVEL");
  const [busca, setBusca] = useState("");
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [nova, setNova] = useState(false);

  const porFase = useMemo(() => {
    const m = {} as Record<Fase, Celula | undefined>;
    for (const f of FASES) m[f] = celulas.find((c) => c.fase === f);
    return m;
  }, [celulas]);
  const total = celulas.reduce((a, c) => a + c.viagens, 0);
  const freteTotal = celulas.reduce((a, c) => a + c.frete, 0);

  // Debounce igual ao de Operações: sem ele cada tecla dispara uma consulta.
  const [buscaDebounced, setBuscaDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounced(busca.trim()), 300);
    return () => clearTimeout(t);
  }, [busca]);

  const ano = competencia ? Number(competencia.slice(0, 4)) : undefined;
  const mes = competencia ? Number(competencia.slice(5, 7)) : undefined;

  const grid = useQuery({
    queryKey: ["torre-grid", empresa, fase, ano, mes, buscaDebounced, vincular],
    enabled: aberto && !!competencia,
    // Reaproveitar a lista anterior evita o "pisca" a cada recarga automática,
    // a cada tecla da busca e a cada troca de mês — mas NÃO pode atravessar
    // uma troca de ABA. Ali as linhas antigas são de OUTRA fase, e ficavam
    // sob o rótulo da fase nova: a aba dizia "47 finalizadas" enquanto a
    // grade listava as 45 disponíveis, cada uma com "a vincular" no
    // motorista. O único sinal era o rodapé "45 de 47".
    //
    // Um instante de spinner é melhor que um instante de informação errada:
    // esta tela existe para dizer o que está acontecendo agora.
    placeholderData: (anterior, anteriorQuery) => {
      const chave = (anteriorQuery?.options?.queryKey ?? []) as unknown[];
      const mesmaAba = chave[1] === empresa && chave[2] === fase;
      return mesmaAba ? anterior : undefined;
    },
    refetchInterval: REFRESH_MS,
    queryFn: async () => {
      const qs = new URLSearchParams({ empresa, fase, limit: "500" });
      if (vincular) qs.set("priorizar_vinculos", "true");
      if (buscaDebounced) qs.set("q", buscaDebounced);
      else if (ano && mes) { qs.set("ano", String(ano)); qs.set("mes", String(mes)); }
      const r = await api.get(`/operacoes?${qs}`);
      return r.data as Record<string, unknown>[];
    },
  });

  const save = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await api.patch(`/entities/viagem_vega/${editing!.id}`, data)).data,
    onSuccess: () => {
      setEditing(null);
      // Vincular motorista MOVE a viagem de aba (a fase é derivada), então
      // as contagens do cabeçalho precisam ser refeitas junto com a grade.
      qc.invalidateQueries({ queryKey: ["torre-grid"] });
      qc.invalidateQueries({ queryKey: ["torre-resumo"] });
    },
  });

  // Cada fase responde a uma pergunta diferente, então mostra colunas
  // diferentes. Uma grade única com 15 colunas (como no portal de referência)
  // obriga a rolar na horizontal para achar o que importa em cada momento.
  const colunas = useMemo(() => {
    const ordem = { key: "numero_ordem", label: "Ordem" };
    const cliente = { key: "cliente_entrega", label: "Cliente" };
    const cidade = { key: "cidade_entrega", label: "Destino" };
    const peso = { key: "peso", label: "Peso (t)", render: (v: unknown) => num(v as number) };
    const coleta = { key: "data_hora_coleta", label: "Coleta (confirmada)", render: fmtDateTime };
    const coletaDia = {
      key: "data_hora_coleta", label: "Coleta (confirmada)",
      render: (v: unknown) => (v ? <b className="whitespace-nowrap text-slate-900">{fmtColetaDia(v)}</b> : <span className="text-slate-400">sem horário</span>),
    };
    const solicitado = { key: "data_hora_solicitada", label: "Solicitado", render: (_v: unknown, row: Record<string, unknown>) => <Solicitado row={row} /> };
    const selos = { key: "_selos", label: "Marcadores", render: (_v: unknown, row: Record<string, unknown>) => <Selos row={row} /> };
    const veiculo = { key: "tipo_veiculo", label: "Veículo" };
    const patio = { key: "patio_coleta", label: "Pátio" };
    const situacao = { key: "status_carga", label: "Situação", render: (v: unknown) => (v ? String(v) : "—") };
    const motorista = {
      key: "motorista", label: "Motorista",
      render: (v: unknown) => (temMotorista(v) ? String(v) : <span className="text-amber-600">a vincular</span>),
    };
    const acoes = {
      key: "_acoes", label: "Ação",
      render: (_v: unknown, row: Record<string, unknown>) =>
        writable ? (
          <button
            className="whitespace-nowrap rounded-lg bg-brand-600 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-700"
            onClick={() => setEditing(row)}
          >
            {temMotorista(row.motorista) ? "editar" : "Vincular carga"}
          </button>
        ) : podeSolicitar && fase !== "FINALIZADA" ? (
          <button
            className="whitespace-nowrap rounded-lg border border-brand-300 px-3 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50"
            onClick={() => setPedindo(row)}
            data-testid="solicitar-horario"
          >
            Solicitar horário
          </button>
        ) : null,
    };

    if (fase === "DISPONIVEL") {
      return [
        acoes, ordem, ...(vincular ? [{key:"score_vinculo",label:"Score do vínculo",render:(v:unknown)=>typeof v === "number" && v > 0 ? <Badge>{`${v}/100`}</Badge> : "Vínculo não comprovado"}] : []), selos, cliente, cidade, veiculo, peso,
        // "Unidade de origem" só existe na USIMINAS (usina que despacha). Numa
        // grade VEGA seria uma coluna inteira de "—" ocupando espaço útil.
        ...(empresa === "USIMINAS" ? [{ key: "unidade_origem", label: "Origem" }] : []),
        patio, solicitado, coleta, situacao,
        { key: "observacao", label: "Observação" },
      ];
    }
    if (fase === "AGENDADA") {
      // A coleta vem logo depois da ação: a grade é lida como uma AGENDA
      // (dia e hora, a próxima primeiro — ver ordemGrid na API).
      return [
        acoes, coletaDia, ordem, selos, motorista,
        { key: "cavalo", label: "Cavalo" },
        cliente, cidade, veiculo, peso, patio, solicitado, situacao,
        { key: "hora_agendamento", label: "Hora ag.", render: fmtHora },
        {
          key: "atraso_coleta", label: "Atraso",
          render: (v: unknown) => (v === "Atraso" ? <Badge>Atraso</Badge> : "—"),
        },
      ];
    }
    return [
      acoes, ordem, selos, motorista, cliente, cidade, veiculo, peso, coleta,
      // Idem: sem frete apurado, a coluna seria uma fileira de "—".
      ...(freteTotal > 0
        ? [{ key: "valor_total_frete", label: "Frete", render: (v: unknown) => brl(v as number) }]
        : []),
    ];
  }, [fase, writable, podeSolicitar, empresa, freteTotal, vincular]);

  // Agendadas: sequência de coleta também no cliente (a busca e o cache
  // anterior não podem embaralhar a agenda).
  const linhas = useMemo(() => {
    const r = grid.data ?? [];
    return fase === "AGENDADA" ? ordenarPorColeta(r as never[]) as Record<string, unknown>[] : r;
  }, [grid.data, fase]);
  const esperado = porFase[fase]?.viagens ?? 0;

  return (
    <Card className="!p-0 overflow-hidden">
      {/* ------------------------------ cabeçalho ------------------------------ */}
      <button
        type="button"
        onClick={onToggle}
        data-testid={`empresa-${empresa}`}
        className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-brand-50/50"
      >
        <span className={`text-lg text-brand-600 transition-transform ${aberto ? "rotate-90" : ""}`}>›</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="text-lg font-bold tracking-tight text-slate-900">{EMPRESA_META[empresa].label}</h2>
            <span className="text-xs text-slate-400">{EMPRESA_META[empresa].descricao}</span>
          </div>
          <div className="mt-2 max-w-md">
            <BarraFases porFase={porFase} total={total} />
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {FASES.map((f) => (
            <ChipFase key={f} fase={f} n={porFase[f]?.viagens ?? 0} testId={`resumo-${empresa}-${f}`} />
          ))}
          <span className="ml-2 text-right text-xs text-slate-400">
            {num(total)} cargas
            <br />
            {/* A tarifa é calculada só para a VEGA (dim.tarifas é a tabela
                dela). Mostrar "R$ 0" na USIMINAS afirmaria um frete zerado
                que ninguém apurou — "sem frete apurado" é o que é verdade. */}
            {freteTotal > 0 ? brlCompact(freteTotal) : <span title="Tarifa apurada apenas na operação VEGA">sem frete apurado</span>}
          </span>
        </div>
      </button>

      {/* -------------------------------- corpo -------------------------------- */}
      {aberto && (
        <div className="border-t border-brand-100 bg-brand-50/30 px-5 py-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {FASES.map((f) => (
              <ChipFase
                key={f}
                fase={f}
                n={porFase[f]?.viagens ?? 0}
                ativo={fase === f}
                onClick={() => setFase(f)}
                testId={`aba-${empresa}-${f}`}
              />
            ))}
            {writable && (
              <button
                type="button"
                className="ml-auto whitespace-nowrap rounded-lg bg-brand-600 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-700"
                onClick={() => setNova(true)}
                data-testid={`nova-ordem-${empresa}`}
              >
                + Nova ordem de coleta
              </button>
            )}
            <input
              className={`input max-w-xs ${writable ? "" : "ml-auto"}`}
              data-testid={`busca-${empresa}`}
              placeholder="Buscar ordem, motorista, cidade…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>

          <p className="mb-3 text-xs text-slate-500" data-testid={`descricao-${empresa}`}>
            {FASE_META[fase].descricao}
            {fase === "DISPONIVEL" && (porFase.DISPONIVEL?.sem_motorista ?? 0) > 0 && (
              <> · <b>{num(porFase.DISPONIVEL!.sem_motorista)}</b> ainda sem motorista</>
            )}
            {fase === "AGENDADA" && <> · em ordem de coleta (dia e hora, a próxima primeiro)</>}
            {fase === "AGENDADA" && (porFase.AGENDADA?.atrasos ?? 0) > 0 && (
              <> · <b className="text-red-600">{num(porFase.AGENDADA!.atrasos)}</b> com atraso de coleta</>
            )}
            {fase === "FINALIZADA" && (
              <> · {num(porFase.FINALIZADA?.peso ?? 0)} t entregues</>
            )}
          </p>

          {grid.isError ? (
            <ErrorBox error={grid.error} />
          ) : grid.isLoading ? (
            <Spinner />
          ) : (
            <>
              {save.isError && <div className="mb-3"><ErrorBox error={save.error} /></div>}
              <Table columns={colunas} rows={linhas} stickyFirst maxHeightClass="max-h-[60vh]" />
              <p className="mt-2 text-xs text-slate-400" data-testid={`contagem-${empresa}`}>
                {buscaDebounced
                  ? `${linhas.length} carga(s) para “${buscaDebounced}” em ${EMPRESA_META[empresa].label} · ${FASE_META[fase].label.toLowerCase()}.`
                  : `${linhas.length} de ${num(esperado)} carga(s).`}
                {/* Uma lista em atualização precisa DIZER que está — senão a
                    divergência momentânea entre o rodapé e a aba passa por
                    erro de contagem. */}
                {grid.isFetching && <span className="ml-2 text-brand-600">atualizando…</span>}
              </p>
            </>
          )}
        </div>
      )}

      {nova && (
        <OrdemColetaModal
          inicial={{ empresa, competencia: competencia ?? undefined }}
          onClose={() => setNova(false)}
          onSalvo={() => {
            setNova(false);
            qc.invalidateQueries({ queryKey: ["torre-grid"] });
            qc.invalidateQueries({ queryKey: ["torre-resumo"] });
          }}
        />
      )}

      {pedindo && <SolicitarHorario row={pedindo} onClose={() => setPedindo(null)} />}

      {editing && (
        <Modal title={`Viagem #${editing.id} — ${EMPRESA_META[empresa].label}`} onClose={() => setEditing(null)}>
          {save.isError && <div className="mb-3"><ErrorBox error={save.error} /></div>}
          <p className="mb-3 rounded-lg bg-brand-50 px-3 py-2 text-xs text-slate-600">
            Vincular um motorista move esta carga de <b>{FASE_META.DISPONIVEL.label.toLowerCase()}</b> para{" "}
            <b>{FASE_META.AGENDADA.label.toLowerCase()}</b>; marcar o status como <b>CARREGADO</b> a leva para{" "}
            <b>{FASE_META.FINALIZADA.label.toLowerCase()}</b>.
          </p>
          <FormEngine
            spec={getEntity("viagem_vega")!}
            initial={editing}
            mode="update"
            onSubmit={(d) => save.mutate(d)}
            onCancel={() => setEditing(null)}
            submitting={save.isPending}
          />
        </Modal>
      )}
    </Card>
  );
}

export function Torre({ vincular = false }: { vincular?: boolean }) {
  const { canWrite, can } = useAuth();
  const [competencia, setCompetencia] = useState<string | null>(null);
  const [aberta, setAberta] = useState<Empresa | null>(null);
  const [novaOrdem, setNovaOrdem] = useState(false);
  const qc = useQueryClient();

  const ano = competencia ? Number(competencia.slice(0, 4)) : undefined;
  const mes = competencia ? Number(competencia.slice(5, 7)) : undefined;

  const resumo = useQuery({
    queryKey: ["torre-resumo", ano, mes, vincular],
    refetchInterval: REFRESH_MS,
    placeholderData: (anterior) => anterior,
    queryFn: async () => {
      const query = new URLSearchParams();
      if (ano && mes) { query.set("ano",String(ano)); query.set("mes",String(mes)); }
      if (vincular) query.set("priorizar_vinculos","true");
      const qs = `?${query}`;
      return (await api.get(`/operacoes/torre${qs}`)).data as Resumo;
    },
  });

  // A competência corrente vem do servidor (a mais recente COM movimento) na
  // primeira carga; depois quem manda é o seletor.
  const competenciaAtiva = competencia ?? resumo.data?.competencia ?? null;

  // Abre sozinho o painel da empresa com mais movimento — a tela já começa
  // mostrando linhas em vez de três cabeçalhos fechados.
  const empresasComDados = useMemo(() => {
    const porEmpresa = new Map<string, number>();
    for (const c of resumo.data?.celulas ?? []) {
      porEmpresa.set(c.empresa, (porEmpresa.get(c.empresa) ?? 0) + c.viagens);
    }
    const positive = (empresa: string) => (resumo.data?.celulas ?? []).filter(c=>c.empresa===empresa && c.fase==="DISPONIVEL").reduce((n,c)=>n+(c.com_sugestao_positiva??0),0);
    return EMPRESAS.filter((e) => (porEmpresa.get(e) ?? 0) > 0)
      .sort((a, b) => (vincular ? positive(b)-positive(a) : 0) || (porEmpresa.get(b) ?? 0) - (porEmpresa.get(a) ?? 0));
  }, [resumo.data, vincular]);

  useEffect(() => {
    if (aberta === null && empresasComDados.length > 0) setAberta(empresasComDados[0]);
  }, [aberta, empresasComDados]);

  const totais = useMemo(() => {
    const t = {} as Record<Fase, number>;
    for (const f of FASES) t[f] = 0;
    for (const c of resumo.data?.celulas ?? []) t[c.fase as Fase] = (t[c.fase as Fase] ?? 0) + c.viagens;
    return t;
  }, [resumo.data, vincular]);

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{vincular ? "Vincular carga" : "Torre de Controle"}</h1>
          <p className="mt-1 text-sm text-slate-500">
            O que está acontecendo agora, por empresa — disponível, agendado e finalizado.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* No topo, e não só dentro de cada empresa: a Torre só lista as
              empresas que JÁ têm carga no mês — a primeira ordem manual da
              USIMINAS não teria onde ser criada. */}
          {!vincular && canWrite("viagem_vega") && (
            <button type="button" className="btn-primary whitespace-nowrap" data-testid="torre-nova-ordem" onClick={() => setNovaOrdem(true)}>
              + Nova ordem de coleta
            </button>
          )}
          {!vincular && <Link to="/torre/planilha" className="btn-secondary whitespace-nowrap" data-testid="torre-visao-planilha">Ver como planilha</Link>}
          <select
            className="input w-44"
            data-testid="torre-competencia"
            value={competenciaAtiva ?? ""}
            onChange={(e) => { setCompetencia(e.target.value); setAberta(null); }}
          >
            {(resumo.data?.competencias ?? []).map((c) => (
              <option key={c} value={c}>{rotuloCompetencia(c)}</option>
            ))}
          </select>
          <div className="text-right text-xs text-slate-400">
            {resumo.isFetching ? (
              <span className="text-brand-600">atualizando…</span>
            ) : resumo.data ? (
              <>atualizado às {new Date(resumo.data.atualizadoEm).toLocaleTimeString("pt-BR")}</>
            ) : null}
            <br />
            recarrega sozinho a cada {REFRESH_MS / 1000}s
          </div>
        </div>
      </div>

      {novaOrdem && (
        <OrdemColetaModal
          inicial={{ competencia: competenciaAtiva ?? undefined }}
          onClose={() => setNovaOrdem(false)}
          onSalvo={() => {
            setNovaOrdem(false);
            setAberta(null);
            qc.invalidateQueries({ queryKey: ["torre-grid"] });
            qc.invalidateQueries({ queryKey: ["torre-resumo"] });
          }}
        />
      )}

      <div className="mb-5 rounded-lg border border-brand-200 bg-brand-50 p-4 text-sm text-slate-700">
        {canWrite("viagem_vega")
          ? <>Abra a empresa, escolha uma carga na aba <b>disponíveis</b> e clique em <b>Vincular carga</b> no início da linha. Confira as sugestões de motorista e placas e clique em <b>Salvar</b>.</>
          : can("vinculo")
          ? <>Use <b>Solicitar horário</b> na linha da carga para pedir a data e a hora de coleta. O pedido fica em <b>Solicitado</b> até a programação confirmar ou alterar o horário aqui na Torre.</>
          : <>Seu perfil permite apenas consultar as cargas. Para vincular ou agendar, peça a alguém com acesso total para ajustar suas permissões.</>}
      </div>

      {vincular && <p className="mb-4 text-sm text-brand-800">Primeiro, cargas com alguma sugestão de score maior que zero, dentro da empresa, situação e período selecionados. As demais continuam disponíveis para vinculação manual.</p>}

      {/* Totais da competência, na mesma ordem das abas. */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {FASES.map((f) => (
          <Card key={f}>
            <div className="flex items-baseline justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{FASE_META[f].label}</div>
              <span className={`badge ${FASE_META[f].tone}`}>{FASE_META[f].label[0]}</span>
            </div>
            <div className="mt-2 text-3xl font-bold text-slate-900" data-testid={`total-${f}`}>
              {num(totais[f] ?? 0)}
            </div>
            <div className="mt-1 text-xs text-slate-400">{FASE_META[f].descricao}</div>
          </Card>
        ))}
      </div>

      {resumo.isError ? (
        <ErrorBox error={resumo.error} />
      ) : resumo.isLoading ? (
        <Spinner />
      ) : empresasComDados.length === 0 ? (
        <Card>
          <p className="text-sm text-slate-500">
            Nenhuma carga em {competenciaAtiva ? rotuloCompetencia(competenciaAtiva) : "—"}. Troque a competência
            ou importe a programação em <b>Programação</b>.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {empresasComDados.map((e) => (
            <PainelEmpresa
              key={e}
              vincular={vincular}
              empresa={e}
              celulas={(resumo.data?.celulas ?? []).filter((c) => c.empresa === e)}
              aberto={aberta === e}
              onToggle={() => setAberta(aberta === e ? null : e)}
              competencia={competenciaAtiva}
            />
          ))}
        </div>
      )}

      <p className="mt-6 text-xs text-slate-400">
        <b>Como classificamos:</b> a fase é derivada, não digitada. Carga com status{" "}
        <i>CARREGADO</i> é finalizada; com status <i>AGENDADO</i> ou com motorista vinculado é agendada; o resto
        está disponível para programar. Isso mantém na tela as cargas cujo status a planilha deixou em branco.
      </p>
    </div>
  );
}
